import React, { FC, ReactElement, useEffect, useState, useMemo } from 'react';
import { Divider, Empty, Flex, Skeleton } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../modules';
import UserRank from '../UserRank/UserRank';
import ContestCard from '../../components/ContestCard';
import { 
    fetchContestDetailRequest, 
    fetchOngoingContestRequest,
    selectLoadingContest, 
    selectContestDetail,
    selectOngoingContest
} from '../../modules/public/contest';
import ParticipantsTable from '../../components/ParticipantsTable';
import { shortenEmail } from '../../helpers';

const PAGE_SIZE = 50;

interface RanksProps {
    contestId?: number;
}

export const Ranks: FC<RanksProps> = ({ contestId }): ReactElement => {
    const dispatch = useDispatch();
    const contestDetail = useSelector((state: RootState) => selectContestDetail(state));
    const ongoingContest = useSelector((state: RootState) => selectOngoingContest(state));
    const loading = useSelector((state: RootState) => selectLoadingContest(state));
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    
    // Use contest detail if contestId provided, otherwise use ongoing contest
    const contest = contestId ? contestDetail : ongoingContest;
    
    // Get rank data and participants from contest detail
    const rankData = contest?.rankList || [];
    const participants = contest?.participants || [];
    const numberOfParticipants = participants.length;
    
    const handleTableChange = (pagination: { current: number; pageSize: number }) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    useEffect(() => {
        if (contestId) {
            // Fetch specific contest detail (includes participants list)
            dispatch(fetchContestDetailRequest(contestId));
        } else {
            // Fallback to ongoing contest for /rank route (includes participants list)
            dispatch(fetchOngoingContestRequest());
        }
    }, [dispatch, contestId]);
    console.log(currentPage, pageSize);
    const columns = useMemo(
        () => [
            {
                title: "Serial",
                dataIndex: "Serial Number",
                key: "serialNumber",
                render: (_: any, __: any, index: number) => (currentPage - 1) * pageSize + index +1,
            },
            {
                title: "Name",
                dataIndex: "participantUsername",
                key: "participantUsername",
            },
            {
                 title: "Email",
                dataIndex: "participantEmail",
                key: "participantEmail",
                render:(item)=> shortenEmail(item)
            }
        ],
        [currentPage, pageSize],
    );

    if (loading) return <Skeleton active />;

    if (!contest || !contest.contestId) {
        return <Empty description="No contest found" />;
    }

    return (
        <Flex vertical gap="2.5rem" style={{ width: '100%' }}>
            <ContestCard contest={contest} loading={loading} numberOfParticipants={numberOfParticipants} />
            <Divider />
            <Flex vertical gap="0.5rem" style={{ width: '100%' }}>
                <h1 className="headline">Faisestock Leaderboard</h1>
                <div className="sub-title">
                    <p>
                        {contest?.contestName.toUpperCase() ?? 'N/A'}
                    </p>
                </div>
                <div className="ranks" style={{ width: '100%' }}>
                    {rankData.length > 0 ? (
                        <UserRank data={rankData} highlightTop={true} />
                    ) : (
                        <Empty description="No rank data available yet. Contest may not have started or no participants have traded." />
                    )}
                </div>
            </Flex>
            <ParticipantsTable
                maxParticipants={contest?.maxParticipants}
                participants={participants}
                currentPage={currentPage}
                pageSize={pageSize}
                columns={columns}
                handleTableChange={handleTableChange}
            />
        </Flex>
    );
};
